import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import { generateInvoice, generateInvoiceNumber } from '../../services/invoiceService';
import {
  getSubscription,
  getTokenBalance,
  getTodayStats,
  getRangeStats,
  getDailySeries,
} from '../../services/shopState';
import { getMyDeals } from '../../services/apiService';
import './ShopStatsPage.css';

const isDemoMode = () => sessionStorage.getItem('walkin_demo_mode') === 'true';

const DAY_LABELS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

const PERIOD_DEFS = [
  { key: 'day',   label: 'Vandaag' },
  { key: 'week',  label: 'Week'    },
  { key: 'month', label: 'Maand'   },
];

// Demo path and offline fallback; live mode reads the backend, see below.
function readMyDeals(shopName) {
  if (!shopName) return [];
  let all = [];
  try { all = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
  catch { all = []; }
  return all.filter((d) => d.shopName === shopName || d.createdBy === shopName);
}

async function loadMyDeals(shopName) {
  if (isDemoMode()) return readMyDeals(shopName);
  try { return await getMyDeals(); }
  catch { return readMyDeals(shopName); }
}

function readRedemptions(shopName) {
  if (!shopName) return [];
  try {
    return JSON.parse(localStorage.getItem(`walkin_redemptions_${shopName}`) || '[]');
  } catch {
    return [];
  }
}

function formatActivityTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

const TOKEN_HISTORY = [
  {
    id: 'TX-10421',
    date: '15-05-2026',
    label: '15 mei',
    pkg: 'Growth',
    description: 'Deal tokens Growth (15x)',
    tokens: 15,
    tokensLabel: '+15 tokens',
    priceEx: '59,95',
    vat: '12,59',
    priceIncl: '72,54',
    paymentMethod: 'iDEAL',
  },
  {
    id: 'TX-10387',
    date: '08-05-2026',
    label: '8 mei',
    pkg: 'Starter',
    description: 'Deal tokens Starter (4x)',
    tokens: 4,
    tokensLabel: '+4 tokens',
    priceEx: '19,95',
    vat: '4,19',
    priceIncl: '24,14',
    paymentMethod: 'iDEAL',
  },
  {
    id: 'TX-10122',
    date: '01-05-2026',
    label: '1 mei',
    pkg: 'Starter',
    description: 'Deal tokens Starter (4x)',
    tokens: 4,
    tokensLabel: '+4 tokens',
    priceEx: '19,95',
    vat: '4,19',
    priceIncl: '24,14',
    paymentMethod: 'iDEAL',
  },
];

const sumEuro = (items, key) => {
  const total = items.reduce(
    (acc, t) => acc + Number(t[key].replace(',', '.')),
    0
  );
  return total.toFixed(2).replace('.', ',');
};

const TOTAL_EX   = sumEuro(TOKEN_HISTORY, 'priceEx');
const TOTAL_VAT  = sumEuro(TOKEN_HISTORY, 'vat');
const TOTAL_INCL = sumEuro(TOKEN_HISTORY, 'priceIncl');

export default function ShopStatsPage() {
  const navigate = useNavigate();
  const shopName = sessionStorage.getItem('walkin_shop_name');
  const [period, setPeriod] = useState('day');
  const [subscription, setSubscription] = useState(getSubscription());
  const [balance, setBalance] = useState(getTokenBalance());
  const [tick, setTick] = useState(0); // forces re-read on storage events
  const [myDeals, setMyDeals] = useState(() => readMyDeals(shopName));
  const bump = () => setTick((t) => t + 1);

  useEffect(() => {
    const refresh = async () => {
      setSubscription(getSubscription());
      setBalance(getTokenBalance());
      bump();
      setMyDeals(await loadMyDeals(shopName));
    };
    refresh();
    window.addEventListener('walkin-token-change', refresh);
    window.addEventListener('walkin-deals-change', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refresh);
    const id = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('walkin-token-change', refresh);
      window.removeEventListener('walkin-deals-change', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', refresh);
      clearInterval(id);
    };
  }, [shopName]);

  // === Stats per period =====================================================
  // tick forces re-evaluation on every refresh tick / storage event.
  void tick;
  let periodStats = { redemptions: 0, views: 0, revenue: 0 };
  if (period === 'day')   periodStats = getTodayStats(shopName);
  if (period === 'week')  periodStats = getRangeStats(shopName, 7);
  if (period === 'month') periodStats = getRangeStats(shopName, 30);

  const conversion = periodStats.views > 0
    ? ((periodStats.redemptions / periodStats.views) * 100).toFixed(1)
    : '0.0';

  const series = getDailySeries(shopName, 7);
  const maxBar = Math.max(1, ...series.map((s) => s.redemptions));

  const bestDeal = myDeals.length > 0
    ? myDeals.reduce((best, d) =>
        (d.redemptions || 0) > (best.redemptions || 0) ? d : best, myDeals[0])
    : null;

  // Still local-only. There is no backend endpoint that lists a retailer's
  // redemptions — GET /redemptions/recent, which getRecentRedemptions() in
  // apiService points at, 404s. Needs a backend route before it can be wired
  // up, same as the dashboard's copy of this.
  const recent = readRedemptions(shopName);

  return (
    <div className="shop-stats">
      <header className="shop-stats__header">
        <button
          className="shop-stats__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-stats__title">Statistieken</h1>
        <span className="shop-stats__spacer" />
      </header>

      <div className="shop-stats__period-tabs">
        {PERIOD_DEFS.map((p) => (
          <button
            key={p.key}
            className={`shop-stats__period-tab${period === p.key ? ' shop-stats__period-tab--active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <main className="shop-stats__main">
        <section className="shop-stats__cards">
          <div className="shop-stats__card">
            <span className="shop-stats__card-label">Totaal bekeken</span>
            <span className="shop-stats__card-value">{periodStats.views.toLocaleString('nl-NL')}</span>
          </div>
          <div className="shop-stats__card">
            <span className="shop-stats__card-label">Totaal ingeleverd</span>
            <span className="shop-stats__card-value">{periodStats.redemptions}</span>
          </div>
          <div className="shop-stats__card">
            <span className="shop-stats__card-label">Conversie</span>
            <span className="shop-stats__card-value">{conversion}%</span>
          </div>
          <div className="shop-stats__card">
            <span className="shop-stats__card-label">Geschatte omzet</span>
            <span className="shop-stats__card-value">€{periodStats.revenue.toLocaleString('nl-NL')}</span>
          </div>
        </section>

        <section className="shop-stats__chart-wrap">
          <div className="shop-stats__chart-head">
            <h2 className="shop-stats__h2">Laatste 7 dagen</h2>
            <span className="shop-stats__chart-meta">Inleveringen</span>
          </div>
          <div className="shop-stats__chart">
            {series.map((s) => (
              <div className="shop-stats__bar-col" key={s.key}>
                <div className="shop-stats__bar-track">
                  <div
                    className="shop-stats__bar"
                    style={{ height: `${(s.redemptions / maxBar) * 100}%` }}
                    title={`${s.redemptions} inleveringen`}
                  >
                    {s.redemptions > 0 && (
                      <span className="shop-stats__bar-value">{s.redemptions}</span>
                    )}
                  </div>
                </div>
                <span className="shop-stats__bar-label">{DAY_LABELS[s.date.getDay()]}</span>
              </div>
            ))}
          </div>
        </section>

        {bestDeal && (bestDeal.redemptions || 0) > 0 ? (
          <section className="shop-stats__best">
            <div className="shop-stats__best-icon">🏆</div>
            <div>
              <div className="shop-stats__best-title">
                Beste deal: {bestDeal.title}
              </div>
              <div className="shop-stats__best-sub">
                {bestDeal.redemptions} keer ingeleverd
              </div>
            </div>
          </section>
        ) : (
          <section className="shop-stats__best shop-stats__best--empty">
            <div className="shop-stats__best-icon">🏆</div>
            <div>
              <div className="shop-stats__best-title">Nog geen beste deal</div>
              <div className="shop-stats__best-sub">
                Zodra klanten een deal inleveren verschijnt hier de topper.
              </div>
            </div>
          </section>
        )}

        <section>
          <h2 className="shop-stats__h2">Recente activiteit</h2>
          {recent.length === 0 ? (
            <div className="shop-stats__activity-empty">
              Nog geen inleveringen.
            </div>
          ) : (
            <ul className="shop-stats__activity">
              {recent.slice(0, 10).map((r) => (
                <li key={r.id} className="shop-stats__activity-item">
                  <span className="shop-stats__activity-time">
                    {formatActivityTime(r.redeemedAt || r.time)}
                  </span>
                  <span className="shop-stats__activity-deal">
                    {r.dealEmoji ? `${r.dealEmoji} ` : ''}{r.dealTitle || '—'}
                  </span>
                  <span className="shop-stats__activity-code">#{r.code}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="shop-stats__tokens-head">
            <h2 className="shop-stats__h2">Token transacties</h2>
            <button
              className="shop-stats__tokens-cta"
              onClick={() => navigate('/shop/tokens')}
            >
              Tokens kopen →
            </button>
          </div>

          <div
            className={
              'shop-stats__sub-pill ' +
              (subscription === 'unlimited' || balance.unlimited
                ? 'shop-stats__sub-pill--unlimited'
                : 'shop-stats__sub-pill--tokens')
            }
          >
            {subscription === 'unlimited' || balance.unlimited
              ? '✅ Unlimited abonnement actief'
              : `🎫 ${balance.available} tokens beschikbaar`}
          </div>
          <ul className="shop-stats__tokens">
            {TOKEN_HISTORY.map((t) => (
              <li key={t.id} className="shop-stats__tokens-item">
                <div className="shop-stats__tokens-main">
                  <span
                    className={`shop-stats__tokens-pkg shop-stats__tokens-pkg--${t.pkg.toLowerCase()}`}
                  >
                    {t.pkg}
                  </span>
                  <div className="shop-stats__tokens-info">
                    <span className="shop-stats__tokens-date">{t.label}</span>
                    <span className="shop-stats__tokens-tokens">{t.tokensLabel}</span>
                  </div>
                </div>
                <div className="shop-stats__tokens-meta">
                  <span className="shop-stats__tokens-amount">€{t.priceEx} ex BTW</span>
                  <span className="shop-stats__tokens-paid">✅ Betaald</span>
                </div>
                <button
                  className="shop-stats__tokens-pdf"
                  aria-label={`Download factuur ${t.id}`}
                  onClick={() => generateInvoice({
                    invoiceNumber: generateInvoiceNumber(t.id),
                    date: t.date,
                    shopName: sessionStorage.getItem('walkin_shop_name') || 'Mijn winkel',
                    email: sessionStorage.getItem('walkin_shop_email') || 'shop@walkin.nl',
                    address: 'Voorbeeldstraat 1',
                    city: '1011 AA Amsterdam',
                    vatNumber: '',
                    packageName: t.pkg,
                    tokens: t.pkg === 'Unlimited' ? 'Onbeperkt' : t.tokens,
                    priceEx: t.priceEx,
                    vat: t.vat,
                    priceIncl: t.priceIncl,
                    paymentMethod: t.paymentMethod,
                    transactionId: t.id,
                  })}
                >
                  ⬇️ PDF
                </button>
              </li>
            ))}
          </ul>

          <div className="shop-stats__tokens-totals">
            <div className="shop-stats__tokens-total-row">
              <span>Totaal ex BTW</span>
              <strong>€{TOTAL_EX}</strong>
            </div>
            <div className="shop-stats__tokens-total-row">
              <span>Totaal BTW (21%)</span>
              <strong>€{TOTAL_VAT}</strong>
            </div>
            <div className="shop-stats__tokens-total-row shop-stats__tokens-total-row--final">
              <span>Totaal incl. BTW</span>
              <strong>€{TOTAL_INCL}</strong>
            </div>
          </div>
        </section>

        <section className="shop-stats__tip">
          <div className="shop-stats__tip-icon">💡</div>
          <div>
            <strong>Tip:</strong> Deals die actief zijn tussen 11:00–13:00
            presteren 40% beter. Overweeg een lunchdeal toe te voegen!
          </div>
        </section>
      </main>

      <ShopBottomNav />
    </div>
  );
}
